import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { APP_ROUTES } from '../../../shared/constants/routes';
import { AuthService } from '../../../features/auth/services/auth.service';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SignalRService } from '../../../shared/services/signal-r.service';
import { AudioPreferenceService } from '../../../core/services/audio-preference.service';
import { UserPreferencesService } from '../../../core/services/user-preferences.service';
import { ThemeService } from '../../../core/services/theme.service';
import {
    decodeJwtPayload,
    extractUserIdentity,
} from '../../../shared/utils/jwt';
import {
    IDENTITY_UNKNOWN_PLACEHOLDER,
    LANDING_OPTIONS,
    PROFILE_MENU_COPY,
    PROFILE_PAGE_SIZE_OPTIONS,
} from '../../../shared/constants/profile-menu';
import {
    lastMergedPrDisplay,
    RELEASE_INFO_COPY,
    releaseTimestampDisplay,
} from '../../../shared/constants/release-info';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Wordmark } from '../../../shared/components/wordmark/wordmark';
import {
    ALERT_DETUNE_CENTS,
    ALERT_GAIN_FLOOR,
    ALERT_GAIN_PEAK,
    ALERT_HARMONIC_RATIO,
    ALERT_PULSE_ATTACK_SECONDS,
    ALERT_PULSE_DURATION_SECONDS,
    ALERT_PULSE_INTERVAL_SECONDS,
    ALERT_RESUME_TIMEOUT_MS,
    ALERT_TONE_HZ,
    MANUAL_APPROVAL_ALERT_DURATION_SECONDS,
} from '../../../shared/constants/alert-audio';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-navbar',
    imports: [
        MatIcon,
        MatToolbar,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatBadgeModule,
        MatTooltipModule,
        MatDividerModule,
        MatFormFieldModule,
        MatSelectModule,
        MatSlideToggleModule,
        Wordmark,
    ],
    templateUrl: './navbar.html',
    styleUrl: './navbar.scss',
    host: {
        '(document:click)': 'unlockAudio()',
        '(document:keydown)': 'unlockAudio()',
    },
})
export class Navbar implements OnDestroy {
    private router = inject(Router);
    private authService = inject(AuthService);
    private signalRService = inject(SignalRService);
    private audioPreferenceService = inject(AudioPreferenceService);
    private userPreferencesService = inject(UserPreferencesService);
    private themeService = inject(ThemeService);

    /** True when the dark scheme is currently applied (drives the toggle). */
    readonly isDarkTheme = computed(() => this.themeService.resolved() === 'dark');

    readonly copy = PROFILE_MENU_COPY;
    readonly identityPlaceholder = IDENTITY_UNKNOWN_PLACEHOLDER;
    readonly landingOptions = LANDING_OPTIONS;
    readonly pageSizeOptions = PROFILE_PAGE_SIZE_OPTIONS;

    /**
     * Release/build metadata (AB#9776, UI-273) — computed once from the
     * build-time constants (real in a production build via
     * build-info.prod.ts, null fallback otherwise). Not derived from
     * `new Date()`, which would show page-open time rather than release
     * time.
     */
    readonly releaseInfoCopy = RELEASE_INFO_COPY;
    readonly releaseTimestamp = releaseTimestampDisplay();
    readonly lastMergedPr = lastMergedPrDisplay();

    /**
     * Identity decoded from the existing access token — no new API. Missing
     * claims stay null so the template renders an explicit unknown state
     * rather than a fabricated value.
     */
    readonly identity = computed(() =>
        extractUserIdentity(decodeJwtPayload(this.authService.getToken())),
    );

    landingRoute = this.userPreferencesService.landingRoute;
    pageSize = this.userPreferencesService.pageSize;
    private audioContext?: AudioContext;
    private audioUnlocked = false;
    /**
     * True when a held-ticket alert could not be played yet because audio was
     * not available at arrival time — the AudioContext had not been created
     * (browser autoplay policy, no user gesture yet) or a suspended-context
     * resume failed/timed out (AB#9470). {@link unlockAudio} flushes it on the
     * next user interaction so the arrival is not silently dropped. It is a
     * one-shot latch (not a queue of ids): a single catch-up chime on unlock is
     * the intended UX, and it is cleared whenever the alarm actually plays.
     */
    private pendingAlert = false;
    private activeAlertOscillators: OscillatorNode[] = [];
    private activeAlertGainNode?: GainNode;
    private activeAlertTimeoutId?: ReturnType<typeof setTimeout>;
    private readonly manualApprovalAlertDurationSeconds =
        MANUAL_APPROVAL_ALERT_DURATION_SECONDS;

    audioEnabled = this.audioPreferenceService.audioEnabled;
    showSoundOnboarding = signal<boolean>(false);
    manualApprovalCount = computed(() => {
        return this.signalRService.getManualApprovalCount();
    });

    constructor() {
        if (!this.audioPreferenceService.onboardingSeen()) {
            this.showSoundOnboarding.set(true);
            // Mark seen on first display so ignoring the popover also
            // suppresses it on later visits.
            this.audioPreferenceService.markOnboardingSeen();
        }

        let lastSignalCount = this.signalRService.getManualApprovalSignal()();
        effect(() => {
            const signalCount = this.signalRService.getManualApprovalSignal()();
            if (signalCount <= lastSignalCount) {
                return;
            }
            lastSignalCount = signalCount;
            if (this.audioEnabled()) {
                this.playNotificationSound();
            }
        });
        effect(() => {
            if (this.manualApprovalCount() === 0) {
                this.stopNotificationSound();
            }
        });

        // Auto-silence on engagement (AB#8756): when any component requests a
        // silence (review-open navigation or an Approve/Reject/Open action),
        // stop the current alert. The global on/off preference is untouched,
        // so a genuinely new held bet arriving afterwards re-triggers the
        // alert via the signal-count effect above.
        let lastSilenceRequest = this.audioPreferenceService.silenceRequest();
        effect(() => {
            const silenceRequest = this.audioPreferenceService.silenceRequest();
            if (silenceRequest <= lastSilenceRequest) {
                return;
            }
            lastSilenceRequest = silenceRequest;
            this.stopNotificationSound();
        });
    }

    ngOnDestroy(): void {
        this.stopNotificationSound();
    }

    /**
     * Silently satisfies the browser's user-gesture requirement for audio
     * playback. Bound to document click/keydown — no dialog is involved.
     */
    unlockAudio(): void {
        // Do NOT short-circuit purely on audioUnlocked: a ticket that arrived
        // before this gesture may have queued a pendingAlert, and this
        // interaction is our chance to flush it. Only skip when we are both
        // already unlocked AND have nothing pending (AB#9470).
        if (
            typeof window === 'undefined' ||
            (this.audioUnlocked && !this.pendingAlert)
        ) {
            return;
        }

        const AudioContextConstructor = this.getAudioContextConstructor();
        if (!AudioContextConstructor) {
            return;
        }

        this.audioContext ??= new AudioContextConstructor();
        if (this.audioContext.state === 'suspended') {
            void this.audioContext
                .resume()
                .then(() => this.onAudioUnlocked())
                .catch((error: unknown) => {
                    console.warn(
                        '[manual-approval-alert] unlock resume rejected; alarm stays queued until the next interaction (AB#9470).',
                        error,
                    );
                });
            return;
        }

        this.onAudioUnlocked();
    }

    /**
     * Completes the audio-unlock handshake once the AudioContext is confirmed
     * running (AB#9470). Marks audio unlocked and, if a held-ticket alert was
     * queued while audio was unavailable, plays that catch-up alarm now instead
     * of dropping the arrival. The pre-fix code set {@link audioUnlocked} only
     * inside the async resume `.then` and never re-fired a raced arrival, so a
     * ticket that landed during the unlock window produced no sound.
     */
    private onAudioUnlocked(): void {
        if (!this.isAudioRunning()) {
            return;
        }

        this.audioUnlocked = true;

        if (this.pendingAlert && this.audioEnabled()) {
            this.pendingAlert = false;
            this.scheduleAlert();
            return;
        }

        // Drop a stale pending flag once audio is available even if the
        // preference is now off, so a later toggle-on doesn't replay an old
        // arrival.
        this.pendingAlert = false;
    }

    openManualApproval() {
        // Navigating to the manual-approval list is NOT engaging with a
        // specific held bet, so it must not silence the alert (AB#8756). The
        // single-pending case routes to the detail screen, whose constructor
        // silences on open; the multi-pending/list case leaves the alert
        // playing until the trader opens an actual bet.
        const pendingItems = this.signalRService.getManualApprovalBacklog()();
        const singlePendingId =
            pendingItems.length === 1 ? pendingItems[0]?.id : null;

        if (singlePendingId) {
            this.router.navigate(
                APP_ROUTES.manualApprovalDetails(singlePendingId),
            );
            return;
        }

        this.router.navigate([APP_ROUTES.manualApprovalList]);
    }

    /**
     * Entry point for a held-ticket arrival. Routes to one of three outcomes,
     * each of which is either an audible alarm or a LOGGED, deliberate skip
     * (AB#9470, AC5 — no more silent returns):
     *  - context running  -> schedule the alarm synchronously;
     *  - context suspended -> await resume (bounded), then schedule (AC2);
     *  - context missing   -> queue a pendingAlert for the next interaction to
     *    flush (AC4 autoplay gate — the browser has not granted audio yet).
     */
    private playNotificationSound(): void {
        if (!this.audioContext) {
            // Autoplay policy: no user gesture yet, so no AudioContext exists.
            // Queue the arrival — unlockAudio() plays it on first interaction.
            this.pendingAlert = true;
            console.warn(
                '[manual-approval-alert] suppressed: no AudioContext yet ' +
                    '(browser autoplay policy — awaiting first user ' +
                    'interaction); alarm queued for unlock (AB#9470).',
            );
            return;
        }

        if (this.isAudioRunning()) {
            this.scheduleAlert();
            return;
        }

        // Context exists but is suspended (backgrounded/idle tab). resume() is
        // asynchronous, so it MUST be awaited before re-checking — the pre-fix
        // synchronous re-check always saw the still-suspended state and dropped
        // the first post-suspend alert (AB#9470, AC2).
        void this.resumeAndSchedule();
    }

    /**
     * Awaits a suspended AudioContext's resume (bounded by
     * {@link ALERT_RESUME_TIMEOUT_MS}) and then schedules the alarm for the
     * SAME arrival. Every non-playing outcome is logged and, where a later
     * gesture could recover it, re-queued as a pendingAlert (AB#9470, AC5).
     */
    private async resumeAndSchedule(): Promise<void> {
        const context = this.audioContext;
        if (!context) {
            return;
        }

        try {
            await this.withTimeout(
                context.resume(),
                ALERT_RESUME_TIMEOUT_MS,
            );
        } catch (error: unknown) {
            this.pendingAlert = true;
            console.warn(
                '[manual-approval-alert] suppressed: AudioContext.resume() ' +
                    'rejected or timed out; alarm queued for the next ' +
                    'interaction (AB#9470).',
                error,
            );
            return;
        }

        if (!this.isAudioRunning()) {
            this.pendingAlert = true;
            console.warn(
                '[manual-approval-alert] suppressed: AudioContext still not ' +
                    'running after resume resolved; alarm queued for the next ' +
                    'interaction (AB#9470).',
            );
            return;
        }

        this.scheduleAlert();
    }

    /**
     * Rejects the given promise if it has not settled within `timeoutMs`, so a
     * resume() that never settles cannot hang the alert path (AB#9470).
     */
    private withTimeout(promise: Promise<unknown>, timeoutMs: number): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const timeoutId = setTimeout(
                () => reject(new Error('AudioContext.resume() timed out')),
                timeoutMs,
            );
            promise.then(
                () => {
                    clearTimeout(timeoutId);
                    resolve();
                },
                (error) => {
                    clearTimeout(timeoutId);
                    reject(error as Error);
                },
            );
        });
    }

    /**
     * Schedules the two-tone Web Audio pulse train for one arrival. Assumes the
     * AudioContext exists and is running — callers (playNotificationSound /
     * resumeAndSchedule / onAudioUnlocked) guarantee that. Replaces any alarm
     * still playing so rapid successive arrivals never stack overlapping
     * oscillators (AB#9470).
     */
    private scheduleAlert(): void {
        if (!this.audioContext) {
            return;
        }

        // A fresh arrival has been (or will be) audibly announced — clear any
        // stale queued flag so it can't double-fire on a later unlock.
        this.pendingAlert = false;

        this.stopNotificationSound();

        const now = this.audioContext.currentTime;
        const alertEndTime = now + this.manualApprovalAlertDurationSeconds;
        const gainNode = this.audioContext.createGain();
        const primaryOscillator = this.audioContext.createOscillator();
        const secondaryOscillator = this.audioContext.createOscillator();
        const pulseIntervalSeconds = ALERT_PULSE_INTERVAL_SECONDS;
        const pulseDurationSeconds = ALERT_PULSE_DURATION_SECONDS;

        primaryOscillator.type = 'square';
        secondaryOscillator.type = 'sawtooth';
        secondaryOscillator.detune.value = ALERT_DETUNE_CENTS;

        gainNode.gain.setValueAtTime(ALERT_GAIN_FLOOR, now);

        for (
            let pulseIndex = 0;
            now + pulseIndex * pulseIntervalSeconds < alertEndTime;
            pulseIndex++
        ) {
            const pulseStart = now + pulseIndex * pulseIntervalSeconds;
            const pulsePeak = pulseStart + ALERT_PULSE_ATTACK_SECONDS;
            const pulseEnd = Math.min(
                pulseStart + pulseDurationSeconds,
                alertEndTime,
            );
            const baseFrequency =
                pulseIndex % 2 === 0 ? ALERT_TONE_HZ[0] : ALERT_TONE_HZ[1];

            primaryOscillator.frequency.setValueAtTime(
                baseFrequency,
                pulseStart,
            );
            secondaryOscillator.frequency.setValueAtTime(
                baseFrequency * ALERT_HARMONIC_RATIO,
                pulseStart,
            );

            gainNode.gain.setValueAtTime(ALERT_GAIN_FLOOR, pulseStart);
            gainNode.gain.linearRampToValueAtTime(ALERT_GAIN_PEAK, pulsePeak);
            gainNode.gain.exponentialRampToValueAtTime(
                ALERT_GAIN_FLOOR,
                pulseEnd,
            );
        }

        primaryOscillator.connect(gainNode);
        secondaryOscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        primaryOscillator.start(now);
        secondaryOscillator.start(now);
        primaryOscillator.stop(alertEndTime);
        secondaryOscillator.stop(alertEndTime);

        this.activeAlertOscillators = [primaryOscillator, secondaryOscillator];
        this.activeAlertGainNode = gainNode;
        this.activeAlertTimeoutId = setTimeout(() => {
            this.stopNotificationSound();
        }, this.manualApprovalAlertDurationSeconds * 1000);
    }

    private stopNotificationSound(): void {
        if (this.activeAlertTimeoutId) {
            clearTimeout(this.activeAlertTimeoutId);
            this.activeAlertTimeoutId = undefined;
        }

        for (const oscillator of this.activeAlertOscillators) {
            try {
                oscillator.stop();
            } catch {
                // Ignore repeated stop calls when replacing an active alert.
            }
            oscillator.disconnect();
        }

        this.activeAlertOscillators = [];
        this.activeAlertGainNode?.disconnect();
        this.activeAlertGainNode = undefined;
    }

    private getAudioContextConstructor(): typeof AudioContext | undefined {
        return (
            window.AudioContext ??
            (window as Window & { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext
        );
    }

    private isAudioRunning(): boolean {
        return this.audioContext?.state === ('running' as AudioContextState);
    }

    /** Flips the app between light and dark (persisted device-wide). */
    toggleTheme(): void {
        this.themeService.toggle();
    }

    toggleAudioEnabled(): void {
        this.audioPreferenceService.toggleAudioEnabled();

        if (!this.audioEnabled()) {
            this.stopNotificationSound();
        }
    }

    dismissSoundOnboarding(): void {
        this.showSoundOnboarding.set(false);
    }

    onLandingRouteChange(route: string): void {
        this.userPreferencesService.setLandingRoute(route);
    }

    onPageSizeChange(size: number): void {
        this.userPreferencesService.setPageSize(size);
    }

    logout() {
        this.authService.logout();
        this.router.navigate([APP_ROUTES.login]);
    }
}
